/**
 * Which chat reply links the landing page chatbot widget opens in place (as a form or hotel-booking
 * modal) instead of following them.
 */

import { SIGNED_LINK_PARAM } from "@/lib/links/signed-link-constants";

/** Canonical in-app link grammars: match inside absolute or relative hrefs alike. */
const CHAT_FORM_LINK_PATTERN = /\/(\d+)\/form\/(\d+)/;
const CHAT_HOTEL_BOOKING_LINK_PATTERN = /\/(\d+)\/hotel-booking\/(\d+)/;

/** Base for resolving relative hrefs; only the query string is read from the result. */
const RELATIVE_HREF_BASE = "https://chat.invalid";

/** Parsed target for a chat reply link the widget can open in place. */
export type InAppLinkTarget =
  | { kind: "form"; eventId: number; formId: number; inviteToken: string | null }
  | { kind: "hotel-booking"; eventId: number; bookingLinkId: number };

/**
 * Detects a form or hotel-booking link in a chat reply href so the widget can open it as an
 * embedded modal instead of navigating away. Works on absolute URLs and relative paths alike. A
 * form link carrying a signed `?e=` token (a link to the viewer's own saved answers) returns null:
 * the modal only understands invite tokens (`rt`), so that link must open the standalone form page,
 * which loads the signed response.
 *
 * @param href - The clicked link's href.
 * @returns The parsed in-app target, or null when the link should be followed normally.
 */
export function parseInAppLink(href: string): InAppLinkTarget | null {
  const formMatch = href.match(CHAT_FORM_LINK_PATTERN);
  if (formMatch) {
    const params = new URL(href, RELATIVE_HREF_BASE).searchParams;
    if (params.has(SIGNED_LINK_PARAM)) return null;
    return {
      kind: "form",
      eventId: parseInt(formMatch[1], 10),
      formId: parseInt(formMatch[2], 10),
      inviteToken: params.get("rt"),
    };
  }
  const hotelMatch = href.match(CHAT_HOTEL_BOOKING_LINK_PATTERN);
  if (hotelMatch) {
    return {
      kind: "hotel-booking",
      eventId: parseInt(hotelMatch[1], 10),
      bookingLinkId: parseInt(hotelMatch[2], 10),
    };
  }
  return null;
}
